import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENTS = ['#FF553B', '#4CDCE6', '#D0FF3F', '#F5C846'];

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

export const Blog = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const activeLang = lang || i18n.language || 'en';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="blog"
      className="relative border-b border-ink pt-16 md:pt-24 pb-24 md:pb-32"
      data-testid="blog-list"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-8">
          <span className="font-mono text-xs bg-ink text-page px-2 py-1 tracking-widest2">§ 07</span>
          <span className="font-mono uppercase text-xs tracking-widest2 text-ink">
            {t('blog.eyebrow')}
          </span>
        </div>

        {/* Heading */}
        <div className="grid grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20">
          <div className="col-span-12 lg:col-span-8">
            <h1 className="display text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-ink">
              {t('blog.h1_pre')}
              <br />
              <span className="serif-italic">{t('blog.h1_italic')}</span>{' '}
              <span
                className="inline-block px-2 md:px-3 text-ink"
                style={{ background: '#D0FF3F' }}
              >
                {t('blog.h1_hl')}
              </span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pt-6">
            <p className="serif-italic text-lg md:text-xl leading-snug text-ink/85">
              {t('blog.lede')}
            </p>
          </div>
        </div>

        {/* Posts grid */}
        {loading ? (
          <p className="serif-italic text-lg text-ink/70">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="box-line p-10 md:p-16 text-center">
            <p className="serif-italic text-lg md:text-xl text-ink/70">{t('blog.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {posts.map((post, i) => {
              const accent = ACCENTS[i % ACCENTS.length];
              return (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                >
                  <Link
                    to={`/${activeLang}/blog/${post.slug}`}
                    className="box-line bg-page block group hover:-translate-y-1 transition-transform duration-500"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden border-b border-ink">
                      {post.cover ? (
                        <img
                          src={post.cover}
                          alt={post.cover_alt || post.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="w-full h-full"
                          style={{ background: `linear-gradient(135deg, #FAF3E3, ${accent}55)` }}
                        />
                      )}
                      <span
                        className="absolute top-3 left-3 w-4 h-4 md:w-5 md:h-5 border border-ink"
                        style={{ background: accent }}
                        aria-hidden
                      />
                    </div>
                    <div className="p-5 md:p-6 flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60">
                          {formatDate(post.date, activeLang)}
                        </span>
                        <span className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </div>
                      <h2 className="font-mono uppercase text-base md:text-lg font-medium text-ink leading-snug mb-3">
                        {post.title}
                      </h2>
                      <p className="serif-italic text-sm md:text-base text-ink/80 leading-snug mb-5">
                        {post.excerpt}
                      </p>
                      <div className="dash-divider pt-4 flex items-center justify-between">
                        <div className="flex flex-wrap gap-1.5">
                          {(post.tags || []).slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="border border-ink/70 px-2 py-1 font-mono uppercase text-[10px] tracking-widest text-ink"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <span className="font-mono uppercase text-xs tracking-widest text-ink group-hover:text-clay transition-colors">
                          {t('blog.readCta')} →
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Footer note */}
        <p className="mt-20 md:mt-24 serif-italic text-base md:text-lg text-ink/70 text-center max-w-2xl mx-auto">
          {t('blog.footerNote')}
        </p>
        <p className="mt-4 text-center">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/rss.xml`}
            target="_blank"
            rel="noreferrer"
            className="font-mono uppercase text-[11px] tracking-widest2 text-ink link-underline"
            data-testid="blog-rss"
          >
            Subscribe · RSS ↗
          </a>
        </p>
      </div>
    </section>
  );
};
